/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/ui/utils/tailwind';
import { tailwindUtils } from '@/shared/ui/utils/tailwind';
import Button from '@/shared/ui/components/basic/Button';
import StatusBadge from '@/shared/ui/components/basic/StatusBadge';
import { Title, Text, Image } from '@/shared/ui';
import type { PartnershipResult } from '../context/PartnershipsWizardContext';

interface ResultCardProps {
  result: PartnershipResult;
}

const currencyFmt = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 0,
});

/**
 * Removes all non-digit characters from a string (used to normalize phone
 * numbers for WhatsApp links).
 *
 * @param value - The raw string, typically a phone number.
 * @returns The string containing only digit characters.
 */
function stripNonDigits(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Maps a normalized advertiser type to its canonical StatusBadge tone.
 *
 * @param advertiserType - owner | agency | broker | unknown (or raw source).
 * @returns The semantic tone for the advertiser badge.
 */
function advertiserTone(advertiserType: string): 'success' | 'info' | 'brand' | 'neutral' {
  if (advertiserType === 'owner') return 'success';
  if (advertiserType === 'agency') return 'info';
  if (advertiserType === 'broker') return 'brand';
  return 'neutral';
}

/**
 * Card that renders a single partner-search result: title, price, key metrics,
 * advertiser contact, and WhatsApp/phone/listing action buttons.
 *
 * @param props - The partnership result to display.
 * @returns The result card.
 */
export function ResultCard({ result }: ResultCardProps) {
  const { t } = useTranslation('features/partnerships');

  const formatPrice = (price: number | null) => {
    if (!price) return '—';
    return currencyFmt.format(price);
  };

  const whatsappLink = result.whatsapp_number
    ? `https://wa.me/55${stripNonDigits(result.whatsapp_number)}`
    : null;

  const fallbackPhone = result.phone_numbers?.[0];
  const firstImage = (result.images ?? []).find(Boolean) ?? null;
  const metrics = [
    result.area_m2 ? { value: `${result.area_m2}`, label: 'm²' } : null,
    result.bedrooms ? { value: `${result.bedrooms}`, label: t('results.bedrooms') } : null,
    result.bathrooms ? { value: `${result.bathrooms}`, label: t('results.bathrooms') } : null,
    result.parking ? { value: `${result.parking}`, label: t('results.parking') } : null,
  ].filter((m): m is { value: string; label: string } => m !== null);

  return (
    <div
      className={cn(
        tailwindUtils.card,
        'p-[var(--space-5)] bg-[var(--surface-raised)] shadow-[var(--shadow-sm)]',
        'flex flex-col gap-[var(--space-4)] sm:flex-row sm:items-stretch',
      )}
    >
      {/* Photo rail — fixed on desktop, full-width on mobile. */}
      <Thumbnail src={firstImage} />

      {/* Content column uses the full remaining width (kills the right void). */}
      <div className="flex min-w-0 flex-1 flex-col gap-[var(--space-3)]">
        {/* Line 1 — title + price (price pinned right). */}
        <div className="flex items-start justify-between gap-[var(--space-4)]">
          <Title order={2} size="md" className="min-w-0 leading-tight">{result.title}</Title>
          <Text span size="lg" fw={700} className="shrink-0 text-[var(--seed-base)]">
            {formatPrice(result.price)}
          </Text>
        </div>

        {/* Line 2 — metrics inline, pipe-separated, no bulky tiles. */}
        {metrics.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-[var(--space-4)] gap-y-[var(--space-1)]">
            {metrics.map((m) => (
              <Text key={m.label} span size="sm" className="text-[var(--text-secondary)]">
                <Text span size="sm" fw={600} className="text-[var(--text-primary)]">{m.value}</Text>
                {' '}{m.label}
              </Text>
            ))}
          </div>
        )}

        {/* Line 3 — advertiser + CRECI + origin/advertiser badges, one row. */}
        <div className="flex flex-wrap items-center gap-x-[var(--space-3)] gap-y-[var(--space-2)]">
          {result.contact_name && (
            <Text span size="sm" fw={500}>{result.contact_name}</Text>
          )}
          {result.creci && (
            <Text span size="xs" className="text-[var(--text-tertiary)]">CRECI {result.creci}</Text>
          )}
          {result.source && result.source !== 'unknown' && (
            <StatusBadge tone={result.source === 'owner_site' ? 'neutral' : 'info'}>
              {t(`results.source_${result.source}`, { defaultValue: result.source })}
            </StatusBadge>
          )}
          {result.advertiser_type && (
            <StatusBadge tone={advertiserTone(result.advertiser_type)}>
              {t(`results.advertiser_${result.advertiser_type}`, { defaultValue: result.advertiser_type })}
            </StatusBadge>
          )}
        </div>

        {/* Actions — inline, aligned left, not full-width stacked. */}
        <div className="mt-auto flex flex-wrap items-center gap-[var(--space-3)] pt-[var(--space-1)]">
          <WhatsAppAction link={whatsappLink} />
          {!result.whatsapp_number && fallbackPhone && (
            <Button
              as="a"
              {...{ href: `tel:${fallbackPhone}` } as Record<string, unknown>}
              variant="outlined"
              size="sm"
            >
              {fallbackPhone}
            </Button>
          )}
          <Button
            as="a"
            {...{ href: result.url, target: '_blank', rel: 'noopener noreferrer' } as Record<string, unknown>}
            variant="outlined"
            size="sm"
          >
            {t('results.view_listing')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The WhatsApp contact action, gated on contact availability.
 *
 * When a number is present the button is a live external link; when it is
 * absent the button is rendered DISABLED (same footprint, no navigation) with
 * a caption telling the agent the information was not found — so a missing
 * contact never silently removes the affordance.
 *
 * @param props - The resolved WhatsApp link, or null when unavailable.
 * @returns The WhatsApp action with its optional unavailable caption.
 */
function WhatsAppAction({ link }: { link: string | null }) {
  const { t } = useTranslation('features/partnerships');
  if (link) {
    return (
      <Button
        as="a"
        data-testid="result-whatsapp"
        {...{ href: link, target: '_blank', rel: 'noopener noreferrer' } as Record<string, unknown>}
        variant="solid"
        size="sm"
      >
        {t('results.whatsapp')}
      </Button>
    );
  }
  return (
    <div className="flex flex-col gap-[var(--space-1)]">
      <Button
        as="button"
        data-testid="result-whatsapp"
        aria-disabled="true"
        disabled
        variant="outlined"
        size="sm"
      >
        {t('results.whatsapp')}
      </Button>
      <Text span size="xs" className="text-[var(--text-tertiary)]">
        {t('results.whatsapp_unavailable_caption')}
      </Text>
    </div>
  );
}

/**
 * Property thumbnail with a graceful no-photo fallback.
 *
 * Renders the image when it loads; on a load error (or when no URL is given)
 * it shows a neutral tile with a house glyph and a "sem foto" caption instead
 * of the browser's broken-image icon — so an offline/missing photo never looks
 * like a rendering defect.
 *
 * @param props - The image URL, or null when the result has no photo.
 * @returns The thumbnail tile.
 */
function Thumbnail({ src }: { src: string | null }) {
  const { t } = useTranslation('features/partnerships');
  const [failed, setFailed] = useState(false);
  const base = 'h-[140px] w-full shrink-0 rounded-(--radius-md) border border-[var(--border-subtle)] sm:h-auto sm:min-h-[140px] sm:w-[160px]';
  if (!src || failed) {
    return (
      <div
        className={cn(base, 'flex flex-col items-center justify-center gap-[var(--space-1)] bg-[var(--surface-tertiary)]')}
        aria-hidden="true"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5">
          <path d="M3 10.5 12 4l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-9.5Z" />
        </svg>
        <Text span size="xs" className="text-[var(--text-tertiary)]">{t('results.no_photo')}</Text>
      </div>
    );
  }
  return (
    <Image
      src={src}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className={cn(base, 'object-cover bg-[var(--surface-tertiary)]')}
    />
  );
}
